import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

/**
 * Workspace content-security settings (Settings → Content Security, CS-4).
 *
 * Mirrors worker/routes/editor.js  GET/PUT /api/editor/workspace-settings.
 * GET is readable by any workspace role (the Create-share flow reads it to
 * prefill defaults + enforce require-passphrase); PUT is owner-only, which the
 * server enforces and the response reports via `canEdit`.
 */

export type WorkspaceSettings = {
  requirePassphrase: boolean;
  defaultExpiryDays: number;
  defaultDownloadAllowed: boolean;
  allowedEmailDomains: string[];
  // Branding (BR-1). Null = fall back to default Editlock branding on guest pages.
  brandName: string | null;
  brandAccent: string | null;      // hex, e.g. #7c5cff
  brandLogoUrl: string | null;     // CDN URL (never the raw R2 key)
  /** True only for the workspace owner — the client hides edit controls otherwise. */
  canEdit: boolean;
};

export type WorkspaceSettingsUpdate = Partial<{
  requirePassphrase: boolean;
  defaultExpiryDays: number;
  defaultDownloadAllowed: boolean;
  allowedEmailDomains: string[];
  brandName: string | null;
  brandAccent: string | null;
  brandLogoKey: string | null;     // commit an uploaded logo, or null to remove
  brandLogoBytes: number;          // uploaded logo size — counts toward storage quota
}>;

const WS_KEY = ["workspace-settings"] as const;

/** GET /api/editor/workspace-settings → effective settings + canEdit. */
export function useWorkspaceSettings() {
  return useQuery<WorkspaceSettings>({
    queryKey: WS_KEY,
    queryFn: () => api.get<WorkspaceSettings>("/editor/workspace-settings"),
    staleTime: 60_000,
  });
}

/** PUT /api/editor/workspace-settings — owner-only; sends only changed fields. */
export function useUpdateWorkspaceSettings() {
  const qc = useQueryClient();
  return useMutation<WorkspaceSettings, Error, WorkspaceSettingsUpdate>({
    mutationFn: (body) => api.put<WorkspaceSettings>("/editor/workspace-settings", body),
    onSuccess: (data) => {
      qc.setQueryData(WS_KEY, data);
      qc.invalidateQueries({ queryKey: WS_KEY });
    },
  });
}

/**
 * Upload a brand logo to R2 (BR-1): presign → direct PUT → return the R2 key.
 * Mirrors the project-cover flow (see use-projects.tsx). The key is committed
 * separately via useUpdateWorkspaceSettings({ brandLogoKey }).
 */
export function useUploadBrandLogo() {
  return useMutation<string, Error, File>({
    mutationFn: async (file) => {
      const presign = await api.post<{ url: string; key: string; expiresIn: number }>(
        "/editor/workspace-settings/brand-logo/presign",
        { contentType: file.type, contentLength: file.size },
      );
      // Content-Type MUST match what was signed or R2 returns SignatureDoesNotMatch.
      const putRes = await fetch(presign.url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!putRes.ok) throw new Error(`Logo upload failed (${putRes.status})`);
      return presign.key;
    },
  });
}
